/**
 * The house date format: "Jun 30, 2026". With a time: "Jun 30, 2026, 3:04 PM".
 * Month only: "Jun 2026". Use these everywhere a date is shown, so screens
 * cannot drift into their own formats.
 *
 * Accepts a Date, an ISO string or epoch milliseconds. Epoch seconds (the BE's
 * launchDate and similar) need `fromEpochSeconds`. Anything unreadable comes
 * back as the fallback.
 */
type DateInput = Date | string | number | null | undefined;

const toDate = (value: DateInput): Date | null => {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const fromEpochSeconds = (seconds: number | null | undefined): Date | null =>
  typeof seconds === 'number' && Number.isFinite(seconds) ? new Date(seconds * 1000) : null;

/** "Jun 30, 2026" */
export const formatDate = (value: DateInput, fallback = ''): string => {
  const d = toDate(value);
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : fallback;
};

/** "Jun 30, 2026, 3:04 PM" */
export const formatDateTime = (value: DateInput, fallback = ''): string => {
  const d = toDate(value);
  if (!d) return fallback;
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${formatDate(d)}, ${time}`;
};

/** "Jun 2026" */
export const formatMonth = (value: DateInput, fallback = ''): string => {
  const d = toDate(value);
  return d ? d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : fallback;
};

/** "Jun 30" (no year), for chart week labels and other places with no room. */
export const formatDayMonth = (value: DateInput, fallback = ''): string => {
  const d = toDate(value);
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : fallback;
};
